import React from 'react';
import { Col, colors, IconButton, Text } from 'common-components';
import { TAvailableRoutesTitles, INavigationEntry } from '@types';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router';

const NavEntriesWrapper = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 16px;
`;
const NavEntry = styled(Col, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isCurrentPage',
})<{ isSelected: boolean; isCurrentPage: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${(props) => (props.isSelected ? '#F9FAFB' : 'transparent')};
  border-radius: 8px;
  padding: 8px;
  @media (max-width: 600px) {
    gap: 0.4em;
  }
  &:hover {
    background: ${colors.gray50};
    cursor: ${(props) => (props.isCurrentPage ? 'not-allowed' : 'pointer')};
  }
`;

const cleanPath = (path: string) => path.replace(/(\.\.\/)+/g, '');
const TypographyWrap = styled(Col)`
  display: flex;
  flex-direction: column;
`;

export const NavEntriesList: React.FC<{
  heading: string
  entries: Array<INavigationEntry>
  selectedRoute: TAvailableRoutesTitles
  iconColor: string
  onEntryClick: () => void
}> = ({ heading, entries, selectedRoute, iconColor, onEntryClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isCurrentPage = (entry: INavigationEntry) => {
    return !!entry.to && location.pathname.endsWith(cleanPath(entry.to));
  };

  return (
    <Col>
      <Text
        size="xs"
        strong
        style={{
          textTransform: 'uppercase',
          color: colors.gray400,
        }}
      >
        {heading}
      </Text>
      <NavEntriesWrapper>
        {entries.map((entry) => (
          <NavEntry
            isSelected={entry.route === selectedRoute}
            isCurrentPage={isCurrentPage(entry)}
            key={entry.route}
            onClick={() => {
              if (entry.to && !isCurrentPage(entry)) {
                onEntryClick();
                navigate(entry.to);
              }
            }}
          >
            <IconButton
              size="sm"
              icon={entry.icon}
              iconProps={{ color: iconColor, width: 24, height: 24 }}
              style={{ borderColor: colors.gray100, padding: 7, boxShadow: 'none' }}
            />
            <TypographyWrap>
              <Text size="sm" color={colors.gray800} style={{ fontWeight: 500 }}>
                {entry.title}
              </Text>
              <Text size="xs" color={colors.gray500}>
                {entry.subtitle}
              </Text>
            </TypographyWrap>
          </NavEntry>
        ))}
      </NavEntriesWrapper>
    </Col>
  );
};
